import {
  has,
  values,
  minBy,
  toNumber,
  isInteger,
  replace,
  forEach,
  forIn,
  isUndefined,
  isObject,
  join,
  compact,
  startsWith
} from 'lodash';
import React, { useState, useEffect, useRef } from 'react';
import { message } from 'antd';
import jwtDecode from 'jwt-decode';
import rp from 'request-promise';
import accents from 'remove-accents';

import countriesData from './../data/countries';
import ImageFedex from 'assets/images/carriers/fedex.svg';
import ImageAramex from 'assets/images/carriers/aramex.svg';
import ImageUps from 'assets/images/carriers/ups.svg';
import ImageSpeedpost from 'assets/images/carriers/speedpost.svg';
import ImageQuantium from 'assets/images/carriers/quantium.svg';
import ImageTnt from 'assets/images/carriers/tnt.svg';
import ImageDpex from 'assets/images/carriers/dpex.svg';
import ImageDhl from 'assets/images/carriers/dhl.svg';
import ImageSkynet from 'assets/images/carriers/skynet.svg';
import ImageZeek from 'assets/images/carriers/zeek.svg';
import ImageMorning from 'assets/images/carriers/morning@1x.png';
import ImageMorning2x from 'assets/images/carriers/morning@2x.png';
import ImageMorning3x from 'assets/images/carriers/morning@3x.png';
import ImageContinental from 'assets/images/carriers/continental.png';
import ImageContinental2x from 'assets/images/carriers/continental@2x.png';
import ImageContinental3x from 'assets/images/carriers/continental@3x.png';

const helpers = {};

helpers.scrollToTop = () => {
  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 0);
};

helpers.scrollToError = target => {
  setTimeout(() => {
    const inputErrors = [
      ...document.querySelectorAll('.ant-form-item-has-error')
    ];

    if (!inputErrors.length) {
      return;
    }

    const elementTop = minBy(
      inputErrors.map(el => el.getBoundingClientRect().top)
    );

    if (!target) {
      window.scrollTo(0, elementTop + window.pageYOffset - 32); // scroll to first error
    } else {
      document
        .querySelectorAll(target)[0]
        .scrollTo(
          0,
          elementTop + document.querySelectorAll(target)[0].scrollTop - 32
        ); // scroll to first error
    }
  }, 0);
};

helpers.isMobile = type => {
  const obj = {
    Android: /Android/i.test(navigator.userAgent),
    // iPadOS: (/iPad/i).test(navigator.userAgent),
    IOS: /iPhone|iPod/i.test(navigator.userAgent),
    Opera: /Opera Mini/i.test(navigator.userAgent),
    Windows: /IEMobile/i.test(navigator.userAgent),
    BlackBerry: /BlackBerry/i.test(navigator.userAgent),
    Screen: window.innerWidth <= 768
  };
  obj.any = values(obj).reduce((a, b) => a || b, false);
  return has(obj, type) ? obj[type] : false;
};

const messageType = {
  quotation: 'Quotation',
  items: 'Declaration',
  pickup: 'Pickup',
  payment: 'Payment',
  auth: 'Auth',
  account: 'Account',
  boxes: 'Boxes'
};

helpers.message = {
  error: (m, p, d) => {
    window.dataLayer.push({
      event: `${messageType[p] || 'Unknown'} Prompt Error`,
      ecommerce: undefined,
      promptError: isObject(m) ? m.message : m,
      errorMessage: isObject(m) ? m.message : m,
      errorCode: isObject(m) ? m.code : p + '-fail'
    });

    message.error(isObject(m) ? m.message : m, d);
  },
  success: (m, p) => {
    window.dataLayer.push({
      event: `${messageType[p] || 'Unknown'} Prompt Success`,
      ecommerce: undefined,
      promptMessage: m
    });

    message.success(m);
  },
  warning: (m, p) => {
    window.dataLayer.push({
      event: `${messageType[p] || 'Unknown'} Prompt Warning`,
      ecommerce: undefined,
      promptMessage: m
    });

    message.warning(m);
  }
};

helpers.formatNumber = (number, precision, format = '{{number}}') => {
  number = toNumber(number);

  let negative = '';
  if (number < 0) {
    negative = '-';
    number = -number;
  }

  const formattedNumber = number
    .toFixed(isInteger(precision) ? precision : 2)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return `${negative}${replace(format, '{{number}}', formattedNumber)}`;
};

helpers.getRouteUrl = (route = '') => {
  const baseUrl = `${process.env.PUBLIC_URL}${
    localStorage.i18nextLng === 'en' ? `/${localStorage.i18nextLng}/` : '/'
  }`;
  return `${baseUrl}${route}`;
};

helpers.responsiveImage = (image, alt = '', width = 'auto') => {
  const data = [];
  forEach(image, (v, k) => {
    data.push(v + ' ' + k);
  });
  return (
    <div
      style={{
        width: width,
        maxWidth: '100%',
        display: 'inline-block',
        verticalAlign: 'middle',
        fontSize: '0'
      }}>
      <img
        src={image['1x']}
        srcSet={data.join(',')}
        style={{ width: '100%' }}
        alt={alt}
        onError={i => (i.target.style.display = 'none')}
      />
    </div>
  );
};

const carrierImage = {
  fedex: {
    '1x': ImageFedex,
    '2x': ImageFedex,
    '3x': ImageFedex
  },
  aramex: {
    '1x': ImageAramex,
    '2x': ImageAramex,
    '3x': ImageAramex
  },
  ups: {
    '1x': ImageUps,
    '2x': ImageUps,
    '3x': ImageUps
  },
  speedpost: {
    '1x': ImageSpeedpost,
    '2x': ImageSpeedpost,
    '3x': ImageSpeedpost
  },
  quantium: {
    '1x': ImageQuantium,
    '2x': ImageQuantium,
    '3x': ImageQuantium
  },
  tnt: {
    '1x': ImageTnt,
    '2x': ImageTnt,
    '3x': ImageTnt
  },
  dpex: {
    '1x': ImageDpex,
    '2x': ImageDpex,
    '3x': ImageDpex
  },
  dhl: {
    '1x': ImageDhl,
    '2x': ImageDhl,
    '3x': ImageDhl
  },
  skynet: {
    '1x': ImageSkynet,
    '2x': ImageSkynet,
    '3x': ImageSkynet
  },
  zeek: {
    '1x': ImageZeek,
    '2x': ImageZeek,
    '3x': ImageZeek
  },
  morning: {
    '1x': ImageMorning,
    '2x': ImageMorning2x,
    '3x': ImageMorning3x
  },
  continental: {
    '1x': ImageContinental,
    '2x': ImageContinental2x,
    '3x': ImageContinental3x
  }
};

const carrierImageWidth = {
  fedex: 77.9,
  aramex: 83.2,
  ups: 27,
  speedpost: 83.6,
  quantium: 82.3,
  tnt: 83.1,
  dpex: 83.2,
  dhl: 83.2,
  skynet: 83.3,
  zeek: 83.2,
  mroning: 83.2,
  continental: 124
};

const carrierText = {
  fedex: 'FedEx',
  aramex: 'Aramex',
  ups: 'UPS',
  speedpost: 'SpeedPost',
  quantium: 'Quantium',
  tnt: 'TNT',
  dpex: 'DPEX',
  dhl: 'DHL',
  skynet: 'Skynet',
  zeek: 'Zeek',
  morning: 'Morning',
  continental: 'Continental'
};

helpers.getCarrierImage = carrier => {
  return carrierImage[carrier] || {};
};

helpers.getCarrierImageWidth = (carrier, ratio = 1) => {
  let factor = 0.6 * ratio;
  if (carrier === 'ups') {
    factor = 0.95 * ratio;
  }

  return carrierImageWidth[carrier]
    ? `${carrierImageWidth[carrier] * factor}px`
    : 'auto';
};

helpers.getCarrierText = carrier => {
  return carrierText[carrier] || '';
};

helpers.getWeightCategory = weight => {
  switch (true) {
    case weight < 2:
      return '0-1 kg';
    case weight < 5:
      return '2-4 kg';
    case weight < 10:
      return '5-9 kg';
    case weight < 21:
      return '10-20 kg';
    case weight < 45:
      return '21-44 kg';
    case weight < 71:
      return '45-70 kg';
    case weight < 100:
      return '71-99 kg';
    case weight < 300:
      return '100-299 kg';
    case weight < 500:
      return '300-499 kg';
    case weight < 1000:
      return '500-999 kg';
    case weight >= 1000:
      return '1000+ kg';
    default:
  }
};

helpers.getTrackingUrl = ref => {
  return `${process.env.PUBLIC_URL}/account/orders/${ref}`;
};

helpers.getCarrierTrackingUrl = (carrier, trackingNumber) => {
  switch (carrier) {
    case 'fedex':
      return `https://www.fedex.com/apps/fedextrack/?action=track&trackingnumber=${trackingNumber}`;
    case 'aramex':
      return `https://www.aramex.com/track/results?mode=0&ShipmentNumber=${trackingNumber}`;
    case 'ups':
      return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    case 'quantium':
      return `http://track.quantiumsolutions.com/?TrackNo=${trackingNumber}`;
    case 'tnt':
      return `https://www.tnt.com/express/zh_hk/site/shipping-tools/tracking.html?searchType=CON&cons=${trackingNumber}`;
    case 'dpex':
      return `https://dpex.com/track-and-trace/?cn=${trackingNumber}`;
    case 'dhl':
      return `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}&brand=DHL`;
    case 'skynet':
      return `https://sky.skynet.net/public/Tracking.aspx?guide=${trackingNumber}`;
    case 'speedpost':
      return 'https://www.hongkongpost.hk/tc/mail_tracking/index.html';
    case 'apg':
      return 'https://apg.etowertech.com/';
    default:
      console.warn(`No tracking URL for carrier ${carrier}`);
      return '';
  }
};

function setState(state, newState) {
  forIn(newState, (value, name) => {
    if (isUndefined(state[`_${name}`])) {
      [state[name], state[`_${name}`]] = useState(value);
    } else {
      state[`_${name}`](value);
    }
  });
}

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

helpers.setState = setState;
helpers.usePrevious = usePrevious;

helpers.checkJwt = rules => {
  try {
    const expireTime = localStorage.getItem('expireTime');
    const now = Math.floor(Date.now() / 1000);
    if (expireTime < now) {
      localStorage.removeItem('token');
      return false;
    }

    return true;
  } catch (e) {
    console.error('Check Token Err:', e);
    return false;
  }
};

helpers.decodeJwt = token => {
  try {
    const decoded = jwtDecode(token);
    return decoded;
  } catch (e) {
    // console.error('Decode Token Err:', e);
    return {};
  }
};

helpers.setJwt = token => {
  try {
    localStorage.setItem('token', token);

    const decoded = helpers.decodeJwt(token);

    const { exp: expireTime, user } = decoded;

    localStorage.setItem('expireTime', expireTime);

    return { token, user };
  } catch (e) {
    console.error('Set Jwt Err:', e);
    return false;
  }
};

helpers.getJwt = () => {
  try {
    const token = helpers.checkJwt() ? localStorage.getItem('token') : null;

    if (!token) {
      return {};
    }
    const decoded = helpers.decodeJwt(token);

    const { user } = decoded;

    return { token, user };
  } catch (e) {
    console.error('Get Jwt Err:', e);
    return false;
  }
};

helpers.getCurrentLang = () => {
  return (window && window.localStorage.i18nextLng) || 'zh';
};

helpers.getCurrentCountry = (uppercase = false) => {
  const country = 'hk'; // support HK for business version only
  return uppercase ? country.toUpperCase() : country;
};

helpers.getCurrentLocale = (uppercase = false) => {
  const country = helpers.getCurrentCountry(uppercase);
  const language = helpers.getCurrentLang();
  const locale = `${language}-${country}`;

  return locale;
};

helpers.rp = async options => {
  const { token } = helpers.getJwt();
  const locale = helpers.getCurrentLocale();

  let headers = {
    platform: 'web',
    language: localStorage.i18nextLng,
    'x-locale': locale,
    'Content-Type': 'application/json'
  };

  const params = { headers, ...options };

  if (typeof token !== 'undefined') {
    headers.Authorization = `Bearer ${token}`;
  }

  const result = await rp(params);

  if (
    result.success === false &&
    result.code === '1100' &&
    options.authorization !== false
  ) {
    // re login
  }

  return result;
};

helpers.mergeAddressStreet1 = (unit, street1) => {
  return join(compact([unit, street1]), ', ');
};

helpers.onlyAscii = str => {
  return accents.remove(str).replace(/[^\x00-\x7F]/g, '');
};

helpers.clearLocalStorage = prefix => {
  if (!prefix) return;

  const arr = []; // Array to hold the keys
  // Iterate over localStorage and insert the keys that meet the condition into arr
  for (let i = 0; i < localStorage.length; i++) {
    if (startsWith(localStorage.key(i), prefix)) {
      arr.push(localStorage.key(i));
    }
  }

  // Iterate over arr and remove the items by key
  for (let i = 0; i < arr.length; i++) {
    localStorage.removeItem(arr[i]);
  }
};

export default helpers;
